"use client";

import { useState, useEffect } from "react";

export default function BalanceSheetPage({
  globalFile,
  globalSheet,
  availableSheets: propSheets = [],
}) {
  const [sourceMode, setSourceMode] = useState("upload");
  const [localFile, setLocalFile] = useState(null);
  const [selectedSheet, setSelectedSheet] = useState("");
  const [localSheets, setLocalSheets] = useState([]);
  const [entities, setEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [htmlContent, setHtmlContent] = useState("");
  const [auditMeta, setAuditMeta] = useState(null);

  const sheets = propSheets.length > 0 ? propSheets : localSheets;
  const file = globalFile || localFile;
  const activeSheet = selectedSheet || globalSheet || (sheets.length > 0 ? sheets[0] : "");

  useEffect(() => {
    let ignore = false;
    async function loadEntities() {
      try {
        const res = await fetch("/api/entities");
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : data.entities || [];
          if (!ignore) {
            setEntities(list);
            if (list.length > 0) {
              setSelectedEntity(list[0].id || list[0].name || list[0]);
            }
          }
        }
      } catch (e) {
        console.warn("Error loading entities:", e);
      }
    }
    loadEntities();
    return () => {
      ignore = true;
    };
  }, []);

  const processFile = async (uploaded, sheetOverride = "") => {
    setLocalFile(uploaded);
    setError("");
    setHtmlContent("");

    const formData = new FormData();
    formData.append("file", uploaded);

    try {
      setLoading(true);
      const res = await fetch("/api/inspect", {
        method: "POST",
        body: formData,
      });
      const data = await res.json();

      if (res.ok && data.sheetNames) {
        setLocalSheets(data.sheetNames);
        const targetSheet =
          sheetOverride ||
          globalSheet ||
          data.activeSheetName ||
          data.sheetNames[0];
        setSelectedSheet(targetSheet);
        if (data.detectedEntityId) setSelectedEntity(data.detectedEntityId);
      }
    } catch (err) {
      console.warn("Workbook auto-inspect error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let ignore = false;

    if (globalFile) {
      const formData = new FormData();
      formData.append("file", globalFile);

      fetch("/api/inspect", {
        method: "POST",
        body: formData,
      })
        .then((res) => res.json())
        .then((data) => {
          if (ignore) return;
          if (data?.sheetNames) {
            setLocalSheets(data.sheetNames);
            const targetSheet =
              globalSheet ||
              data.activeSheetName ||
              data.sheetNames[0];
            setSelectedSheet(targetSheet);
            if (data.detectedEntityId) setSelectedEntity(data.detectedEntityId);
          }
        })
        .catch((err) => {
          if (!ignore) {
            console.warn("Workbook auto-inspect error:", err);
          }
        });
    }

    return () => {
      ignore = true;
    };
  }, [globalFile, globalSheet]);

  const handleGenerate = async (e) => {
    e?.preventDefault();

    setLoading(true);
    setError("");
    setHtmlContent("");
    setAuditMeta(null);

    try {
      if (sourceMode === "upload") {
        if (!file) throw new Error("Please select an Excel workbook file.");

        const formData = new FormData();
        formData.append("file", file);
        if (activeSheet) formData.append("sheet", activeSheet);
        if (selectedYear) formData.append("year", selectedYear);

        const res = await fetch("/api/balance-sheet", {
          method: "POST",
          body: formData,
        });
        const data = await res.json();

        if (!res.ok)
          throw new Error(
            data.error || "Failed to generate Balance Sheet from file.",
          );

        setHtmlContent(data.html);
        setAuditMeta({
          periods: data.report.periodKeys,
          sheet: data.activeSheetName,
          entity: data.detectedEntityId || "From Uploaded File",
          report: data.report,
        });
      } else {
        if (!selectedEntity)
          throw new Error("Please select an Entity ID from the database.");

        const params = new URLSearchParams({
          entityId: selectedEntity,
          ...(selectedYear ? { year: selectedYear } : {}),
        });

        const res = await fetch(`/api/balance-sheet?${params.toString()}`);
        const data = await res.json();

        if (!res.ok)
          throw new Error(
            data.error || "Failed to fetch Balance Sheet from database.",
          );

        setHtmlContent(data.html);
        setAuditMeta({
          periods: data.report.periodKeys,
          entity: data.entityId,
          report: data.report,
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 p-6 md:p-10">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-5">
            <div>
              <h1 className="text-2xl font-bold text-slate-800">
                Balance Sheet Generator
              </h1>
              <p className="text-sm text-slate-500">
                Generate cumulative Balance Sheets dynamically from direct
                spreadsheet uploads or database records.
              </p>
            </div>

            <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-lg self-start">
              <button
                type="button"
                onClick={() => {
                  setSourceMode("upload");
                  setError("");
                }}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                  sourceMode === "upload"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Upload Excel File
              </button>

              <button
                type="button"
                onClick={() => {
                  setSourceMode("database");
                  setError("");
                }}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                  sourceMode === "database"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Database Entity
              </button>
            </div>
          </div>

          <form
            onSubmit={handleGenerate}
            className="mt-6 grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 gap-4 items-end"
          >
            {sourceMode === "upload" ? (
              <>
                <div className="sm:col-span-2">
                  {!globalFile ? (
                    <>
                      <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">
                        Excel Workbook File
                      </label>
                      <input
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) processFile(f);
                        }}
                        className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer border border-slate-300 rounded-lg"
                      />
                    </>
                  ) : (
                    <div className="text-sm text-slate-600 font-medium">
                      Using Workspace File:{" "}
                      <span className="text-sky-600">{globalFile.name}</span>
                    </div>
                  )}
                </div>

                {/* Local Sheet Selector */}
                {sheets.length > 0 && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">
                      Sheet{" "}
                      {globalSheet && activeSheet === globalSheet
                        ? "(Global)"
                        : "(Overridden)"}
                    </label>
                    <select
                      value={activeSheet}
                      onChange={(e) => setSelectedSheet(e.target.value)}
                      className={`w-full px-3 py-2 bg-white border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 ${
                        activeSheet === globalSheet
                          ? "border-slate-300"
                          : "border-amber-500 text-amber-700"
                      }`}
                    >
                      {sheets.map((sh, i) => (
                        <option key={i} value={sh}>
                          {sh}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </>
            ) : (
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">
                  Saved Entity
                </label>
                {entities.length > 0 ? (
                  <select
                    value={selectedEntity}
                    onChange={(e) => setSelectedEntity(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    {entities.map((ent, idx) => {
                      const id = typeof ent === "object" ? ent.id : ent;
                      const name =
                        typeof ent === "object" ? ent.name || ent.id : ent;
                      return (
                        <option key={idx} value={id}>
                          {name} ({id})
                        </option>
                      );
                    })}
                  </select>
                ) : (
                  <input
                    type="text"
                    placeholder="e.g. HOLDCO"
                    value={selectedEntity}
                    onChange={(e) => setSelectedEntity(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  />
                )}
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">
                Filter Year (Optional)
              </label>
              <input
                type="number"
                placeholder="e.g. 2026"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={loading || (sourceMode === "upload" && !file)}
                className="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white font-semibold text-sm rounded-lg shadow-sm transition-colors"
              >
                {loading ? "Processing..." : "Generate Balance Sheet"}
              </button>
            </div>
          </form>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
              {error}
            </div>
          )}
        </div>

        {auditMeta && (
          <div className="flex flex-wrap items-center justify-between gap-4 px-4 py-3 bg-slate-200/60 rounded-lg text-xs text-slate-700">
            <div>
              <span className="font-semibold">Entity:</span> {auditMeta.entity}
              {auditMeta.sheet && (
                <span className="ml-3">
                  <span className="font-semibold">Sheet:</span>{" "}
                  {auditMeta.sheet}
                </span>
              )}
              <span className="ml-3">
                <span className="font-semibold">Evaluated Periods:</span>{" "}
                {auditMeta.periods.join(", ")}
              </span>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => window.print()}
                className="px-3 py-1 bg-white hover:bg-slate-100 border border-slate-300 font-semibold rounded shadow-sm text-slate-800"
              >
                Print / Save PDF
              </button>
            </div>
          </div>
        )}

        {htmlContent && (
          <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
          </div>
        )}
      </div>
    </div>
  );
}